import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle, ArrowRight, Calendar, Sparkles } from "lucide-react";
import { SEO } from "@/components/seo/SEO";
import { useEntitlement } from "@/hooks/useEntitlement";
import { supabase } from "@/integrations/supabase/client";
import { differenceInDays, format } from "date-fns";

const SubscriptionSuccess = () => {
  const navigate = useNavigate();
  const { userId, examSlug, expiresAt, trialExpiresAt } = useEntitlement();
  
  // Use trialExpiresAt from useEntitlement instead of fetching separately
  const hasActiveTrial = trialExpiresAt && new Date(trialExpiresAt) > new Date();
  const daysRemaining = trialExpiresAt ? Math.max(0, differenceInDays(new Date(trialExpiresAt), new Date())) : 0;

  return (
    <>
      <SEO 
        title="Subscription Activated - Welcome to Medical MCQ Access"
        description="Your medical student subscription has been successfully activated. Start practicing with our comprehensive MCQ database."
      />
      
      <div className="container py-12">
        <div className="max-w-2xl mx-auto text-center">
          <Card className="border-emerald-200 bg-emerald-50/50">
            <CardHeader className="pb-4">
              <div className="w-16 h-16 mx-auto mb-4 bg-emerald-100 rounded-full flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-emerald-600" />
              </div>
              <CardTitle className="text-2xl text-emerald-800">
                Subscription Activated!
              </CardTitle>
            </CardHeader>
            
            <CardContent className="pt-0">
              <p className="text-lg text-emerald-700 mb-6">
                {hasActiveTrial 
                  ? `Your 3-day free trial has started! You have ${daysRemaining} days to explore all premium features.`
                  : "Your subscription is now active and you have full access to all premium features."
                }
              </p>

              {hasActiveTrial && (
                <div className="p-4 bg-emerald-100 rounded-lg border border-emerald-300 mb-6 flex items-start gap-3">
                  <Sparkles className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-emerald-800 mb-1">Free Trial Active</p>
                    <p className="text-sm text-emerald-700">
                      You won't be charged until your trial ends. Cancel anytime during the trial period.
                    </p>
                  </div>
                </div>
              )}
              
              <div className="space-y-4 mb-8">
                <div className="p-4 bg-white rounded-lg border border-emerald-200">
                  <h3 className="font-semibold text-emerald-800 mb-2">What's included:</h3>
                  <ul className="text-sm text-emerald-700 space-y-1 text-left">
                    <li>• Full access to 2000+ medical MCQs</li>
                    <li>• Detailed explanations and references</li>
                    <li>• Progress tracking and analytics</li>
                    <li>• Mock exam simulations</li>
                    <li>• Priority customer support</li>
                  </ul>
                </div>

                {(trialExpiresAt || expiresAt) && (
                  <div className="p-4 bg-white rounded-lg border border-emerald-200">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-emerald-700 flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        {hasActiveTrial ? "Trial ends" : "Next billing date"}
                      </span>
                      <span className="font-medium text-emerald-800">
                        {format(new Date(hasActiveTrial ? trialExpiresAt! : expiresAt!), 'MMM dd, yyyy')}
                      </span>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  onClick={() => navigate('/practice')}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  Start Practicing
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => navigate('/dashboard')}
                  className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                >
                  View Dashboard
                </Button>
              </div>
              
              <p className="text-sm text-emerald-600 mt-6">
                You can manage your subscription anytime from your dashboard.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
};

export default SubscriptionSuccess;